import * as Yup from 'yup';
import i18next from 'i18next';
import { isValidAge } from '../../util/date-utils';
import React, { FC, ReactElement, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { TherapistOnboardingRequest } from '../../../../shared/requests/therapistOnboarding.request';
import { DateTime } from 'luxon';

type FormValues = {
  firstName: string;
  lastName: string;
  phone: string;
  street: string;
  postal: string;
  city: string;
  country: string;
  birthdayDate: string;
  birthdayMonth: string;
  birthdayYear: string;
}

type AccountSetupFormProps = {
  onSuccess: () => void;
}

const phoneRegex: RegExp = /^[+]*[(]?[0-9]{1,3}[)]?[-.0-9]{8,14}$/;

const AccountSetupForm: FC<AccountSetupFormProps> = ({ onSuccess }: AccountSetupFormProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    phone: '',
    street: '',
    postal: '',
    city: '',
    country: i18next.t('accountSetup.countryList').split(',')[0],
    birthdayDate: '',
    birthdayMonth: '',
    birthdayYear: '',
  };
  
  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    firstName: Yup.string().required(i18next.t('accountSetup.firstNameRequired')),
    lastName: Yup.string().required(i18next.t('accountSetup.lastNameRequired')),
    phone: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')).matches(phoneRegex, i18next.t('accountSetup.invalidPhone')),
    street: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')),
    postal: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')),
    city: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')),
    country: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')),
    birthdayDate: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')),
    birthdayMonth: Yup.string().required(i18next.t('accountSetup.pleaseFillIn')),
    birthdayYear: Yup.string().required(i18next.t('accountSetup.pleaseFillIn'))
  }).test('valid-age', i18next.t('accountSetup.ageValidation'), function (values: FormValues) {
    const { birthdayDate, birthdayMonth, birthdayYear } = values ?? {};

    if (!birthdayDate || !birthdayMonth || !birthdayYear) {
      return true;
    }

    if (!isValidAge(birthdayDate, birthdayMonth, birthdayYear)) {
      return this.createError({ path: 'birthdayDate', message: i18next.t('accountSetup.ageValidation') });
    }

    return true;
  });

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);
    
    const birthday: DateTime = DateTime.fromObject({
      year: Number(values.birthdayYear),
      month: Number(values.birthdayMonth),
      day: Number(values.birthdayDate),
    })
    
    if (!birthday.isValid) {
      
      setErrorMessage('accountSetup.ageValidation');
      return;
    }

    try {
      const request: TherapistOnboardingRequest = { 
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        birthday: birthday.toUTC().toISO() ?? '',
        street: values.street,
        postal: values.postal,
        city: values.city,
        country: values.country,
      };
      
      const id: string = await authFetch<string>('/api/v1/user-profile/account-setup', { method: 'POST', body: request });
      onSuccess();
      
    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };
  
  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >{({ values, handleChange, errors, touched, handleSubmit, isSubmitting, submitForm }: FormikProps<FormValues>) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label column={false}>{i18next.t('accountSetup.firstName')}</Form.Label>
                  <Form.Control
                    defaultValue={values.firstName}
                    type="text"
                    name="firstName"
                    id="firstName"
                    isInvalid={touched.firstName ? !isEmpty(errors.firstName) : false}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.firstName}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label column={false}>{i18next.t('accountSetup.lastName')}</Form.Label>
                  <Form.Control
                    defaultValue={values.lastName}
                    type="text"
                    name="lastName"
                    id="lastName"
                    isInvalid={touched.lastName ? !isEmpty(errors.lastName) : false}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.lastName}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label column={false}>{i18next.t('accountSetup.street')}</Form.Label>
                  <Form.Control
                    defaultValue={values.street}
                    type="text"
                    name="street"
                    id="street"
                    isInvalid={touched.street ? !isEmpty(errors.street) : false}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.street}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label column={false}>{i18next.t('accountSetup.postal')}</Form.Label>
                  <Form.Control
                    defaultValue={values.postal}
                    type="text"
                    name="postal"
                    id="postal"
                    isInvalid={touched.postal ? !isEmpty(errors.postal) : false}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.postal}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{i18next.t('accountSetup.city')}</Form.Label>
                  <Form.Control
                    defaultValue={values.city}
                    type="text"
                    name="city"
                    id="city"
                    isInvalid={touched.city ? !isEmpty(errors.city) : false}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.city}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>{i18next.t('accountSetup.telephone')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    id="phone"
                    isInvalid={touched.phone ? !isEmpty(errors.phone) : false}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.phone}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>{i18next.t('accountSetup.country')}</Form.Label>
                  <Form.Control as="select"
                    value={values.country}
                    type="text"
                    name="country"
                    id="country"
                    isInvalid={touched.country ? !isEmpty(errors.country) : false}
                    onChange={handleChange}
                  > {
                      i18next.t('accountSetup.countryList').split(',').map((val, i) => {
                        return (
                          <option key={'country-' + i} value={val}>{val}</option>
                        );
                      })}</Form.Control>
                  <Form.Control.Feedback className="fw-bold" type="invalid" role="alert">{errors.country}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-0">
              <Col>
                <Form.Group className="mb-0">
                  <Form.Label>{i18next.t('accountSetup.dateOfBirth')}</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={2} xs={3} >
                <Form.Group className="mb-0">
                  <Form.Control
                    type="text"
                    name="birthdayDate"
                    id="birthdayDate"
                    placeholder="DD"
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={2} xs={4}>
                <Form.Group className="mb-0">
                  <Form.Control
                    type="text"
                    name="birthdayMonth"
                    id="birthdayMonth"
                    placeholder="MM"
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3} xs={5}>
                <Form.Group className="mb-0">
                  <Form.Control
                    type="text"
                    name="birthdayYear"
                    id="birthdayYear"
                    placeholder="YYYY"
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group> {errors.birthdayDate ? (<div className="d-block invalid-feedback fw-bold">{errors.birthdayDate}</div>) : null}</Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="float-end">
                  <Button type="button" variant="primary" className="mt-3 float-end" onClick={submitForm} disabled={isSubmitting || isSaving}>
                    {i18next.t('accountSetup.completeRegistration')}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  )
  
}

export default AccountSetupForm;

